import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export interface HeroSlide {
  id: string;
  title: string;
  enTitle: string;
  year: number;
  rating: number;
  genres: string[];
  summary: string;
  backdrop: string;
}

interface HeroCarouselProps {
  slides: HeroSlide[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const go = (next: number) => {
    if (slides.length === 0) return;
    setCurrent((next + slides.length) % slides.length);
  };

  useEffect(() => {
    if (slides.length <= 1) return;
    timer.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [slides.length]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(current + (delta < 0 ? 1 : -1));
    }
    touchStartX.current = null;
  };

  if (slides.length === 0) {
    return (
      <section className="w-full">
        <div className="flex h-[420px] w-full items-center justify-center bg-primary-900 md:h-[600px]">
          <p className="text-sm text-background-200">暂无焦点内容</p>
        </div>
      </section>
    );
  }

  const slide = slides[current];

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={s.backdrop}
            alt={`${s.title} 剧照`}
            title={`${s.title} 韩国伦理剧`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/60 to-primary-950/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-primary-950/10" />

      <div className="relative mx-auto flex h-[420px] w-full max-w-[1200px] flex-col justify-end px-4 pb-16 md:h-[600px] md:px-6 md:pb-20">
        <div key={slide.id} className="max-w-2xl animate-soft-fade">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-fire-line text-sm" />
            本季焦点 · 韩国伦理剧
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            《{slide.title}》
          </h1>
          <p className="mt-2 text-sm font-medium uppercase tracking-[0.2em] text-background-300">
            {slide.enTitle} · {slide.year}
          </p>
          <p className="mt-4 hidden max-w-xl text-sm leading-relaxed text-background-200 md:block md:text-base">
            {slide.summary}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-100">
              <i className="ri-star-fill text-accent-400" />
              {slide.rating.toFixed(1)}
            </span>
            {slide.genres.map((g) => (
              <span key={g} className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-100">
                {g}
              </span>
            ))}
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${slide.id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-circle-line text-lg" />
              查看剧情详情
            </Link>
            <Link
              to="/category/ethical-drama"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
            >
              浏览全部伦理剧
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(current - 1)}
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-md bg-background-50/15 text-background-50 transition-colors hover:bg-background-50/30 md:flex md:left-6"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(current + 1)}
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-md bg-background-50/15 text-background-50 transition-colors hover:bg-background-50/30 md:flex md:right-6"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            aria-label={`切换到第${i + 1}张`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === current ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
            }`}
          />
        ))}
      </div>
    </section>
  );
}